import { useEffect, useRef, useState } from 'react';
import { LogIn, Server, KeyRound, Mail, Delete, PackageCheck, Check } from 'lucide-react';
import { api, setToken, getServerUrl, setServerUrl } from '../api';
import { Input, useToast } from '../components/ui';

/**
 * Prihlásenie baliaceho stola.
 *
 * ── Prečo vlastná obrazovka ──────────────────────────────────────────────
 * Pri stole je dotykový displej, klávesnica žiadna a Windows si svoju
 * klávesnicu na obrazovke vyvolá, kedy sa mu zachce. Bežné prihlásenie by tam
 * znamenalo ťukať do políčka a čakať, či niečo vyskočí. Preto je číselnica
 * priamo na stránke: veľké tlačidlá, nič iné na obrazovke nie je.
 *
 * Políčko na e-mail tu nie je zámerne. Konto stola sa prihlasuje **kódom** –
 * systém podľa neho pozná, o koho ide. Kto sa chce prihlásiť svojím kontom,
 * má dole odkaz; nie je to skryté, len to nie je to prvé, na čo sa ťuká.
 *
 * Tmavá je od začiatku: stôl beží v nočnom režime a preblik do bieleho pri
 * prihlásení by bol jediné miesto, kde displej ráno pichne do očí.
 */
function StolPrihlasenie({ onLogin, onInak }) {
  const toast = useToast();
  const [kod, setKod] = useState('');
  const [busy, setBusy] = useState(false);
  const pole = useRef(null);

  /* Fyzická klávesnica má fungovať tiež – pri stole občas býva a naťukať kód
   * je rýchlejšie než ťukať prstom. Preto je pod číselnicou skutočné políčko,
   * len nie je vidieť ako políčko. */
  useEffect(() => { pole.current?.focus(); }, []);

  const prihlas = async (e) => {
    e?.preventDefault?.();
    if (busy || !kod) return;
    setBusy(true);
    try {
      const res = await api.post('/api/auth/login', { password: kod });
      setToken(res.token);
      onLogin(res.user);
    } catch (err) {
      toast.error(err.message);
      setKod('');
      pole.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const klavesa = 'flex h-20 items-center justify-center rounded-2xl bg-slate-800 text-3xl '
    + 'font-semibold text-slate-100 ring-1 ring-slate-700 transition active:bg-slate-700';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
      <form onSubmit={prihlas} className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <PackageCheck size={34} className="shrink-0 text-brand-500" />
          <div>
            <h1 className="text-2xl font-bold leading-tight text-white">Baliaci stôl</h1>
            <p className="text-sm text-slate-400">Zadajte kód stola</p>
          </div>
        </div>

        {/* Zadané znaky ako bodky. Kód sa pri stole ťuká pred ľuďmi – vidieť
            ho netreba, ale vidieť, koľko znakov už je, áno. */}
        <div className="flex h-20 items-center justify-center rounded-2xl bg-slate-900 ring-1 ring-slate-700"
          onClick={() => pole.current?.focus()}>
          {kod
            ? <span className="text-4xl tracking-[0.35em] text-white">{'•'.repeat(kod.length)}</span>
            : <span className="text-lg text-slate-600">– – – – –</span>}
        </div>

        {/* Skutočné políčko: drží fyzickú klávesnicu aj čítačku pri živote.
            Nevidno ho, ale je zaostrené. */}
        <input
          ref={pole}
          className="h-0 w-0 border-0 bg-transparent p-0 opacity-0"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={kod}
          onChange={(e) => setKod(e.target.value.slice(0, 32))}
        />

        <div className="mt-4 grid grid-cols-3 gap-3">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((c) => (
            <button key={c} type="button" className={klavesa}
              onClick={() => setKod((k) => (k + c).slice(0, 32))}>{c}</button>
          ))}
          <button type="button" className={klavesa} onClick={() => setKod((k) => k.slice(0, -1))}>
            <Delete size={26} />
          </button>
          <button type="button" className={klavesa}
            onClick={() => setKod((k) => (k + '0').slice(0, 32))}>0</button>
          <button type="submit" disabled={busy || !kod}
            className="flex h-20 items-center justify-center rounded-2xl bg-brand-600 text-white
                       ring-1 ring-brand-500 transition active:bg-brand-500 disabled:opacity-40">
            <Check size={30} />
          </button>
        </div>

        <div className="mt-5 text-center">
          <button type="button" className="text-xs text-slate-500 hover:text-slate-300" onClick={onInak}>
            <Mail size={12} className="mr-1 inline" /> Prihlásiť sa e-mailom
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Login({ onLogin, stol = false }) {
  /* Pri stole sa začína číselnicou; odkaz „Prihlásiť sa e-mailom" prepne na
   * bežné prihlásenie pre toho, kto sem prišiel so svojím kontom. */
  const [akoStol, setAkoStol] = useState(stol);
  if (akoStol) return <StolPrihlasenie onLogin={onLogin} onInak={() => setAkoStol(false)} />;
  return <BeznePrihlasenie onLogin={onLogin} />;
}

function BeznePrihlasenie({ onLogin }) {
  const toast = useToast();
  const [quick, setQuick] = useState(null); // null = ešte nevieme, aký režim server používa
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showServer, setShowServer] = useState(!!getServerUrl());
  const [server, setServer] = useState(getServerUrl());

  useEffect(() => {
    api.get('/api/auth/mode')
      .then((m) => setQuick(!!m.quick_login))
      .catch(() => setQuick(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (showServer) setServerUrl(server);
      // v režime pre predajňu sa posiela len kód, systém podľa neho pozná osobu
      const res = await api.post('/api/auth/login', quick ? { password } : { email, password });
      setToken(res.token);
      onLogin(res.user);
    } catch (err) {
      toast.error(err.message);
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-7 shadow-xl">
        {/* Prihlásenie je prvé, čo zamestnanec ráno vidí – nech tam je značka.
            Keby logo chýbalo, ostane pôvodný nápis a prihlásiť sa dá tak či tak. */}
        <div className="mb-6">
          <img src="/uploads/logo-steps-cervene.png" alt="STEPS STORE" className="h-9 w-auto"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <p className="mt-2 text-sm text-slate-500">Objednávky, ponuky a servis</p>
        </div>

        {quick === null ? (
          <div className="py-6 text-center text-sm text-slate-400">Načítavam…</div>
        ) : (
          <>
            <div className="space-y-3">
              {quick ? (
                <Input label="Prístupový kód" type="password" inputMode="text" autoFocus required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  hint="Zadajte svoj kód – systém podľa neho pozná, kto pracuje." />
              ) : (
                <>
                  <Input label="E-mail" type="email" autoFocus required
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                  <Input label="Heslo" type="password" required
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                </>
              )}

              {showServer && (
                <Input label="Adresa servera" value={server} onChange={(e) => setServer(e.target.value)}
                  placeholder="https://crm.steps.sk"
                  hint="Nechajte prázdne, ak systém beží na tomto počítači." />
              )}
            </div>

            <button className="btn-primary mt-5 w-full btn-lg" disabled={busy}>
              <LogIn size={18} /> {busy ? 'Prihlasujem…' : 'Prihlásiť sa'}
            </button>

            <div className="mt-3 flex items-center justify-center gap-4">
              <button type="button"
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600"
                onClick={() => { setQuick((q) => !q); setPassword(''); }}>
                {quick ? <><Mail size={13} /> Prihlásiť sa e-mailom</> : <><KeyRound size={13} /> Prihlásiť sa kódom</>}
              </button>
              <button type="button"
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600"
                onClick={() => setShowServer((s) => !s)}>
                <Server size={13} /> {showServer ? 'Skryť' : 'Nastaviť'} server
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
